/**
 * MemoryPage — V9.8 (refonte)
 *
 * Page publique du module Mémoire :
 *  - Liste les `memory_items` publiés (`/api/memory`).
 *  - Filtres : type, discipline, année.
 *  - Liens vers la fiche détail `/memoire/{slug}`.
 *
 * Fallback historique (timeline anciennes compétitions) retiré au profit
 * du contenu administrable. Si aucun élément n'est encore publié, on
 * affiche un EmptyState invitant l'admin à créer du contenu.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, BookOpen, Archive, Trophy, Image as ImageIcon, User, Calendar, ExternalLink, Library } from "lucide-react";

const MEMORY_TYPES = [
  { value: "article", label: "Article", icon: BookOpen },
  { value: "archive", label: "Archive sportive", icon: Archive },
  { value: "portrait_historique", label: "Portrait historique", icon: User },
  { value: "competition_ancienne", label: "Compétition ancienne", icon: Trophy },
  { value: "photo_video", label: "Photo / vidéo", icon: ImageIcon },
  { value: "temoignage", label: "Témoignage", icon: User },
  { value: "palmares_historique", label: "Palmarès historique", icon: Trophy },
  { value: "evenement_marquant", label: "Événement marquant", icon: Calendar },
];

export default function MemoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [disciplineFilter, setDisciplineFilter] = useState("all");
  const [disciplines, setDisciplines] = useState([]);
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    api.get("/disciplines").then((r) => setDisciplines(r.data.items || [])).catch(() => {});
    api.get("/site-settings").then((r) => setSiteSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { limit: 100 };
    if (typeFilter !== "all") params.type = typeFilter;
    if (disciplineFilter !== "all") params.discipline = disciplineFilter;
    api
      .get("/memory", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [typeFilter, disciplineFilter]);

  // Grouper par année (décroissant), "Sans date" en dernier
  const grouped = useMemo(() => {
    const byYear = {};
    items.forEach((m) => {
      const key = m.year ? String(m.year) : "Sans date";
      (byYear[key] = byYear[key] || []).push(m);
    });
    const ordered = Object.entries(byYear).sort(([a], [b]) => {
      if (a === "Sans date") return 1;
      if (b === "Sans date") return -1;
      return Number(b) - Number(a);
    });
    return ordered;
  }, [items]);

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-8 aito-editorial-motif" data-testid="memory-page">
      <h1 className="font-display text-3xl sm:text-4xl font-bold flex items-center gap-2">
        <Sparkles className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
        Mémoire du sport polynésien
      </h1>
      <p className="text-sm text-muted-foreground mt-1">
        Articles, archives, portraits et événements marquants du sport polynésien.
      </p>

      {/* V11.9 — Bannière visuelle Histoire des Jeux du Pacifique */}
      {siteSettings?.memoire?.banner_image_url && (
        <Card className="mt-6 overflow-hidden aito-card-hover" data-testid="memory-banner">
          <div className="relative aspect-[21/9] sm:aspect-[21/8] bg-black">
            <img
              src={siteSettings.memoire.banner_image_url}
              alt={siteSettings.memoire.banner_title || "Histoire des Jeux du Pacifique"}
              className="absolute inset-0 h-full w-full object-cover opacity-95"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 text-white">
              <div className="flex items-center gap-2 mb-1">
                <Trophy className="h-4 w-4 text-[hsl(var(--aito-gold))]" />
                <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px] uppercase tracking-wider">Histoire</Badge>
              </div>
              <h2 className="font-display text-xl sm:text-2xl font-bold" data-testid="memory-banner-title">
                {siteSettings.memoire.banner_title || "L'Histoire des Jeux du Pacifique"}
              </h2>
              {siteSettings.memoire.banner_subtitle && (
                <p className="text-sm text-white/90 mt-1 max-w-2xl">{siteSettings.memoire.banner_subtitle}</p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* V11.9 — Bibliothèque : livres d'histoire du sport polynésien */}
      {siteSettings?.memoire?.books?.length > 0 && (
        <section className="mt-8" data-testid="memory-books-section">
          <h2 className="font-display font-bold text-lg sm:text-xl flex items-center gap-2 mb-3">
            <Library className="h-5 w-5 text-[hsl(var(--aito-lagoon))]" />
            Livres · Histoire du sport polynésien
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {siteSettings.memoire.books.map((book, i) => {
              const inner = (
                <Card className="overflow-hidden aito-card-hover h-full flex flex-col" data-testid={`memory-book-${i}`}>
                  <div className="aspect-[2/3] bg-muted relative overflow-hidden">
                    {book.cover_url ? (
                      <img src={book.cover_url} alt={book.title} className="absolute inset-0 h-full w-full object-cover object-top" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center text-muted-foreground">
                        <BookOpen className="h-12 w-12 opacity-30" />
                      </div>
                    )}
                    {book.external_url && (
                      <span className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1">
                        <ExternalLink className="h-3 w-3" />
                      </span>
                    )}
                  </div>
                  <div className="p-3 flex-1">
                    <div className="font-display font-semibold text-sm leading-tight line-clamp-2">{book.title}</div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {book.author}{book.year ? ` · ${book.year}` : ""}
                    </div>
                    {book.description && (
                      <p className="text-[11px] text-muted-foreground mt-1 line-clamp-2">{book.description}</p>
                    )}
                  </div>
                </Card>
              );
              return book.external_url ? (
                <a key={i} href={book.external_url} target="_blank" rel="noreferrer" data-testid={`memory-book-link-${i}`}>{inner}</a>
              ) : (
                <div key={i}>{inner}</div>
              );
            })}
          </div>
        </section>
      )}

      {/* Filtres */}
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-xl">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="memory-filter-type"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les types</SelectItem>
            {MEMORY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={disciplineFilter} onValueChange={setDisciplineFilter}>
          <SelectTrigger data-testid="memory-filter-discipline"><SelectValue placeholder="Discipline" /></SelectTrigger>
          <SelectContent className="max-h-60">
            <SelectItem value="all">Toutes les disciplines</SelectItem>
            {disciplines.map((d) => <SelectItem key={d.slug} value={d.slug}>{d.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Liste */}
      <div className="mt-6">
        {loading ? (
          <Card className="p-6 text-center text-sm text-muted-foreground">Chargement…</Card>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Sparkles}
            title="La mémoire commence par les premiers contributions."
            description="Aucun élément publié pour ces filtres. Revenez bientôt — l'équipe AITO.PF enrichit la mémoire du sport polynésien régulièrement."
          />
        ) : (
          <div className="relative pl-6 before:absolute before:left-2 before:top-0 before:h-full before:w-px before:bg-border">
            {grouped.map(([year, group]) => (
              <div key={year} className="mb-6">
                <span className="sticky top-16 inline-flex rounded-full bg-[hsl(var(--aito-black))] text-[hsl(var(--aito-gold))] px-3 py-1 text-xs font-mono z-10">
                  {year}
                </span>
                <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                  {group.map((m) => (
                    <MemoryCard key={m.id} item={m} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MemoryCard({ item }) {
  const typeMeta = MEMORY_TYPES.find((t) => t.value === item.type) || MEMORY_TYPES[0];
  const Icon = typeMeta.icon;
  return (
    <Link to={`/memoire/${item.slug}`} className="block group" data-testid="memory-card">
      <Card className="overflow-hidden aito-card-hover">
        {item.image_url && (
          <div className="aspect-[16/9] bg-muted overflow-hidden">
            <img
              src={item.image_url}
              alt={item.title}
              loading="lazy"
              className="w-full h-full object-cover object-top transition group-hover:scale-105"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
        )}
        <div className="p-4">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Badge variant="outline" className="text-[10px] gap-1">
              <Icon className="h-3 w-3" /> {typeMeta.label}
            </Badge>
            {item.discipline && <span className="text-[11px] text-muted-foreground capitalize">· {item.discipline}</span>}
          </div>
          <div className="font-display font-semibold leading-snug group-hover:text-[hsl(var(--aito-lagoon))]">{item.title}</div>
          {item.short_description && <p className="text-xs text-muted-foreground line-clamp-3 mt-1">{item.short_description}</p>}
        </div>
      </Card>
    </Link>
  );
}
